import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useApi, useRouteRef } from '@backstage/core-plugin-api';
import {
  Content,
  Header,
  Link,
  Page,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { CircularProgress, Typography, makeStyles } from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import { spassuCodeApiRef } from '../../api';
import type { Activity } from '../../api/types';
import {
  controlPlaneConnectRouteRef,
  controlPlaneNewRouteRef,
} from '../../routes';
import { normalizeRouteRefPath } from '../../utils/routes';

export interface ConnectDiscontinuedPageProps {
  activityId: string;
}

const useStyles = makeStyles(theme => ({
  loadingCenter: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 240,
  },
  stack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

interface ActivitySnapshot {
  activity: Activity | null;
  error: Error | null;
  loading: boolean;
  /** Id a que este snapshot pertence; ver `useActivitySnapshot`. */
  activityId: string | null;
}

function useActivitySnapshot(activityId: string): ActivitySnapshot {
  const api = useApi(spassuCodeApiRef);
  const [snapshot, setSnapshot] = useState<ActivitySnapshot>({
    activity: null,
    error: null,
    loading: true,
    activityId: null,
  });

  useEffect(() => {
    let cancelled = false;
    setSnapshot({
      activity: null,
      error: null,
      loading: true,
      activityId: null,
    });
    api
      .getActivity(activityId)
      .then(result => {
        if (cancelled) return;
        setSnapshot({
          activity: result,
          error: null,
          loading: false,
          activityId,
        });
      })
      .catch((loadError: unknown) => {
        if (cancelled) return;
        setSnapshot({
          activity: null,
          error:
            loadError instanceof Error
              ? loadError
              : new Error('Erro ao carregar atividade'),
          loading: false,
          activityId,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [activityId, api]);

  // O efeito só zera o snapshot DEPOIS do render: navegar entre ids (as rotas
  // /connect/:id e /cli/:id compartilham este componente) pintaria a atividade
  // ANTERIOR sob o id novo por um frame — anunciando a semântica de
  // descontinuação errada. Snapshot de outro id conta como carregando.
  if (snapshot.activityId !== activityId) {
    return { activity: null, error: null, loading: true, activityId: null };
  }

  return snapshot;
}

const DiscontinuedNotice = ({ connected }: { connected: boolean }) => (
  <div role="status" aria-live="polite">
    {/* `role="presentation"` nos Alerts: o padrão do MUI é `role="alert"`
        (assertivo), que criaria uma região live interna e anularia a
        prioridade `polite` contratada para esta região — o aviso é
        informativo, não interruptivo. */}
    {connected ? (
      <Alert severity="info" role="presentation">
        <Typography variant="body1" component="p">
          A operação desta atividade não foi afetada pela retirada: a ingestão
          de telemetria segue aceita enquanto a credencial do agente for válida,
          e a atividade continua listada no portfólio com status e via de
          trabalho, como antes. A conexão guiada e o painel CLI dedicado foram
          descontinuados — se a credencial expirar, não há reconexão pela via
          legada; a orientação é migrar para control-plane.
        </Typography>
      </Alert>
    ) : (
      <Alert severity="warning" role="presentation">
        <Typography variant="body1" component="p">
          A conexão guiada desta via foi descontinuada. Não é mais possível
          conectar um terminal por aqui — sessões de onboarding pendentes não
          podem mais ser concluídas.
        </Typography>
      </Alert>
    )}
    <Typography variant="body1" component="p">
      O caminho suportado para conectar um terminal é a via Control Plane: crie
      uma atividade nessa via e conecte pela tela dedicada de pareamento.
    </Typography>
  </div>
);

const CreateControlPlaneAction = () => {
  const controlPlaneNewRoute = useRouteRef(controlPlaneNewRouteRef);
  if (!controlPlaneNewRoute) {
    return (
      <Typography variant="body2" color="textSecondary" component="p">
        Use o menu de criação de atividades para criar uma atividade na via
        Control Plane.
      </Typography>
    );
  }
  return (
    <Typography variant="body1" component="p">
      <Link to={normalizeRouteRefPath(controlPlaneNewRoute())}>
        Criar atividade na via Control Plane
      </Link>
    </Typography>
  );
};

const DiscontinuedShell = ({
  title = 'Conexão descontinuada',
  subtitle,
  children,
}: {
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
}) => (
  <Page themeId="tool">
    <Header title={title} subtitle={subtitle} />
    <Content>{children}</Content>
  </Page>
);

/**
 * Página única de descontinuação da conexão guiada legada (ADR-005).
 *
 * - Atividade control-plane: redirect incondicional para a tela nova de
 *   conexão (sem leitura de feature flag).
 * - Demais work paths: aviso acessível de descontinuação nomeando a via
 *   control-plane, com variante para atividade já conectada. Nenhum comando,
 *   credencial ou código de pareamento é emitido.
 */
const ControlPlaneRouteUnavailable = () => (
  <DiscontinuedShell title="Conexão desta atividade fica na via Control Plane">
    {/* Mesmo piso de a11y do aviso: região `status` polite e `Alert` sem papel
        assertivo próprio (o padrão do MUI é `role="alert"`). */}
    <div role="status" aria-live="polite">
      <Alert severity="warning" role="presentation">
        <Typography variant="body1" component="p">
          A conexão desta atividade não foi descontinuada — ela acontece na tela
          dedicada da via Control Plane, que não está disponível nesta
          instalação do portal. Verifique se o plugin da via está montado.
        </Typography>
      </Alert>
    </div>
  </DiscontinuedShell>
);

export const ConnectDiscontinuedPage = (
  props: ConnectDiscontinuedPageProps,
) => {
  const { activityId } = props;
  const classes = useStyles();
  const controlPlaneConnectRoute = useRouteRef(controlPlaneConnectRouteRef);
  const { activity, error, loading } = useActivitySnapshot(activityId);

  if (loading) {
    // Título neutro enquanto o work path é desconhecido: atividade
    // control-plane vai REDIRECIONAR para a tela nova, e anunciar "Conexão
    // descontinuada" antes disso afirma o oposto do que vai acontecer.
    return (
      <DiscontinuedShell title="Carregando atividade">
        <div className={classes.loadingCenter}>
          <CircularProgress />
        </div>
      </DiscontinuedShell>
    );
  }

  if (error || !activity) {
    // Mesmo motivo do estado de carregamento: sem conhecer o work path, rotular
    // a tela como "Conexão descontinuada" pode estar afirmando o oposto — a
    // atividade pode ser control-plane e seguir funcionando.
    return (
      <DiscontinuedShell title="Atividade indisponível">
        <ResponseErrorPanel
          error={error ?? new Error('Atividade não encontrada')}
        />
      </DiscontinuedShell>
    );
  }

  if (activity.workPath === 'control-plane') {
    // O route ref da tela nova é `optional: true`: sem vínculo no app, ele
    // resolve `undefined`. Cair no aviso de descontinuação seria AFIRMAR que a
    // conexão control-plane foi descontinuada — o oposto do verdadeiro. Nesse
    // caso a tela diz que o destino não está alcançável nesta instalação.
    if (!controlPlaneConnectRoute) return <ControlPlaneRouteUnavailable />;
    return (
      <Navigate
        to={normalizeRouteRefPath(controlPlaneConnectRoute({ activityId }))}
        replace
      />
    );
  }

  return (
    <DiscontinuedShell
      subtitle={activity.title ? `Atividade: ${activity.title}` : undefined}
    >
      <div className={classes.stack}>
        <DiscontinuedNotice connected={activity.handoffState === 'connected'} />
        <CreateControlPlaneAction />
      </div>
    </DiscontinuedShell>
  );
};
